/**
 * Whether a bet placed right now could ever be settled.
 *
 * `posture` AND `allSettleable` ARE ONE READING, NEVER TWO. The alert condition
 * is `posture === 'live' && !allSettleable`; under `synthetic-only` an
 * unsettleable report is the demo registry being a demo registry, not an
 * incident. Rendering the red without the posture beside it trains an operator
 * to ignore the red.
 *
 * `withheldMarketInstances` IS A TRAFFIC DIAGNOSTIC AND IS LABELLED AS ONE.
 * It counts how many times a row was withheld while serving requests, so its
 * derivative tracks request rate, not coverage — a quiet Tuesday and a fixed
 * gate look the same. `withheldRowKeys` is the book state; that is the list to
 * read and the only one to act on.
 *
 * `cataloguePosture.syncedAt` SEPARATES TWO IDENTICAL-LOOKING FAILURES. A
 * marker from four minutes ago under a superseded posture is 0033's designed
 * 15-minute activation window closing on its own. One from four hours ago is a
 * stalled scheduler. `never_synced` is kept distinct from `stale` rather than
 * collapsed with it: both refuse placement the same way, but the first points
 * at the scheduler and the second at a deploy, and collapsing them sends an
 * operator to the wrong service.
 *
 * This screen is a REPORT. The money path compares the two posture ids itself,
 * in `validateMarketForPlacement`; nothing here is a second decision. That is
 * true of the four diagnostics added below it too — adapter health, the provider
 * registry, the TTL caches and the identity gaps are all read-only and none of
 * them is consulted at placement.
 *
 * THE FOUR DIAGNOSTICS LOAD INDEPENDENTLY OF THE COVERAGE REPORT AND OF EACH
 * OTHER. A live probe is the read most likely to fail, and failing it into the
 * page-level error state would blank the settleability report — which is the one
 * thing on this screen an operator is here to read. So each renders its own
 * state in its own card, and a card that could not be read says so rather than
 * rendering empty: a failed probe and a clean probe must never look the same.
 */
import { useState } from 'react';
import {
  useAliasGaps,
  useInvalidateOddsCache,
  useOddsCache,
  useOddsHealth,
  useOddsProviders,
  useSettlementCoverage,
} from '@/hooks/useAdmin';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Pill } from '@/components/ui/StatusPill';
import { Modal, ActionError } from '@/components/ui/Modal';
import { AdminLoading, AdminError, AdminPageHeader } from './AdminAsync';
import {
  cacheHitRate,
  describeAliasTable,
  describeCacheInvalidation,
  describeMarketGap,
  summariseAdapterHealth,
} from './logic';

function Keys({ items, empty }: { items: string[]; empty: string }) {
  if (items.length === 0) return <p className="text-sm text-white/35">{empty}</p>;
  return (
    <div className="flex flex-wrap gap-1.5">
      {items.map((k) => (
        <span
          key={k}
          className="rounded-lg border border-white/10 bg-white/[0.04] px-2 py-0.5 font-mono text-[11px] text-white/60"
        >
          {k}
        </span>
      ))}
    </div>
  );
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <div className="text-[10px] font-semibold uppercase tracking-[0.08em] text-white/35">
        {label}
      </div>
      <div className="mt-0.5 text-sm text-white/80">{value}</div>
    </div>
  );
}

function timeAgo(iso: string, now: number): string {
  const secs = Math.max(0, Math.round((now - new Date(iso).getTime()) / 1000));
  if (secs < 60) return `${secs}s ago`;
  if (secs < 3600) return `${Math.round(secs / 60)}m ago`;
  return `${Math.round(secs / 3600)}h ago`;
}

/**
 * The per-card state for the four independent diagnostics.
 *
 * A card that FAILED to load says so and offers a re-read. It deliberately does
 * not fall through to the empty rendering — "no adapters reported a problem" and
 * "the probe could not be reached" are opposite readings and must not share a
 * pixel.
 */
function Probe({
  loading,
  failed,
  onRetry,
  children,
}: {
  loading: boolean;
  failed: boolean;
  onRetry: () => void;
  children: React.ReactNode;
}) {
  if (loading) return <p className="text-sm text-white/35">Reading…</p>;
  if (failed)
    return (
      <div className="flex flex-wrap items-center gap-3">
        <p className="text-sm text-white/55">
          This could not be read. That is not a clean result — it is no result.
        </p>
        <Button variant="secondary" size="sm" onClick={onRetry}>
          Try again
        </Button>
      </div>
    );
  return <>{children}</>;
}

function ratePct(rate: number | null): string {
  /* Null is "nothing has been read", NOT zero — see `cacheHitRate`. */
  return rate === null ? 'no reads yet' : `${Math.round(rate * 100)}%`;
}

export function OddsEnginePage() {
  const query = useSettlementCoverage();
  const health = useOddsHealth();
  const providers = useOddsProviders();
  const cache = useOddsCache();
  const aliasGaps = useAliasGaps();

  /* Hooks stay above the early returns — order must not depend on the fetch. */
  const invalidate = useInvalidateOddsCache();
  const [invalidateOpen, setInvalidateOpen] = useState(false);

  if (query.isLoading) return <AdminLoading label="Reading the registry" />;
  if (query.isError || !query.data)
    return <AdminError error={query.error} onRetry={() => void query.refetch()} />;

  const r = query.data;
  /* Age is relative to the fetch, not to render — see MarketVisibilityPage. */
  const now = query.dataUpdatedAt;
  /** The alert condition, verbatim. Synthetic-only gaps are not incidents. */
  const alerting = r.posture === 'live' && !r.allSettleable;

  const cp = r.cataloguePosture;
  /** Which posture inputs differ — names the cause instead of just the state. */
  const providerDrift =
    cp.persistedProviderIds.join(',') !== r.quoteGate.countedProviderIds.join(',');

  return (
    <>
      <AdminPageHeader
        title="Odds engine"
        subtitle="Which sports and markets can actually be settled, what the quote gate is withholding, and whether the catalogue has caught up with the running code."
        actions={
          <Button
            variant="secondary"
            size="sm"
            loading={query.isFetching}
            onClick={() => void query.refetch()}
          >
            Re-read
          </Button>
        }
      />

      <div className="space-y-5">
        {/* Posture and settleability are ONE reading — see the header. */}
        <div
          className={`rounded-2xl border p-4 ${
            alerting
              ? 'border-red-400/35 bg-red-500/[0.06]'
              : 'border-white/[0.08] bg-brand-surface/60'
          }`}
        >
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2.5">
              <Pill tone={r.posture === 'live' ? 'positive' : 'muted'}>{r.posture}</Pill>
              <Pill tone={r.allSettleable ? 'positive' : 'caution'}>
                {r.allSettleable ? 'all settleable' : 'gaps present'}
              </Pill>
            </div>
            <span className="text-xs text-white/40">
              minimum agreement <span className="text-white/70 tabular-nums">{r.minAgreement}</span>{' '}
              providers
            </span>
          </div>

          <p className="mt-3 text-sm leading-relaxed text-white/65">
            {alerting ? (
              <>
                <span className="font-semibold text-red-300">This is the alert condition.</span> The
                registry is live and at least one market cannot reach quorum. Bets on it will be
                taken, will look healthy on the board, and will not settle — they ride the 30-day
                sweeper and are voided.
              </>
            ) : r.posture === 'synthetic-only' ? (
              <>
                No live provider is registered, so the synthetic adapters are the entire product and
                they settle each other. A gap reported here is not an incident — alert only when{' '}
                <span className="text-white/85">posture is live</span> and settleability is false.
              </>
            ) : (
              <>Every market the engine quotes has enough independent results providers to settle.</>
            )}
          </p>
        </div>

        <Card title="Gaps">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-white/35">
                Sports that cannot settle
              </div>
              <Keys items={r.unsettleableSports} empty="None." />
            </div>
            <div>
              <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-white/35">
                Markets that cannot settle
              </div>
              <Keys
                items={r.unsettleableMarkets.map((m) => `${m.sport}:${m.marketType}`)}
                empty="None."
              />
            </div>
          </div>
          <p className="mt-3 text-xs leading-relaxed text-white/35">
            Coverage is per MARKET, not per sport. An adapter advertises what it can QUOTE
            separately from what it can RESULT, so a sport can be fully covered on the board and
            unsettleable on one market type.
          </p>
        </Card>

        <div className="space-y-3">
          {r.sports.map((s) => (
            <Card key={s.sport} title={s.sport}>
              <div className="mb-3 flex flex-wrap items-center gap-2">
                <Pill tone={s.settleable ? 'positive' : 'caution'}>
                  {s.settleable ? 'settleable' : 'not settleable'}
                </Pill>
                <span className="text-[11px] text-white/35">
                  live: {s.liveProviderIds.join(' · ') || 'none'}
                </span>
                {s.syntheticProviderIds.length > 0 && (
                  <span className="text-[11px] text-white/25">
                    synthetic: {s.syntheticProviderIds.join(' · ')}
                  </span>
                )}
              </div>

              <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                {s.markets.map((m) => (
                  <div
                    key={m.marketType}
                    className="rounded-xl border border-white/[0.07] bg-white/[0.02] p-3"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-mono text-[11px] text-white/75">{m.marketType}</span>
                      <Pill tone={m.settleable ? 'positive' : 'caution'}>
                        {m.settleable ? 'ok' : 'no quorum'}
                      </Pill>
                    </div>
                    {m.singleSource && (
                      <span className="mt-1.5 inline-block rounded-lg border border-brand-gold/25 bg-brand-gold/[0.06] px-2 py-0.5 text-[10px] text-brand-gold">
                        single source
                      </span>
                    )}
                    <div className="mt-1.5 font-mono text-[10px] leading-relaxed text-white/25">
                      {m.resultProviderIds.join(' · ') || 'no results provider'}
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          ))}
        </div>

        <Card title="Quote gate">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Field
              label="Mode"
              value={
                <Pill tone={r.quoteGate.mode === 'withhold' ? 'positive' : 'caution'}>
                  {r.quoteGate.mode}
                </Pill>
              }
            />
            <Field label="Posture" value={r.quoteGate.posture} />
            <Field
              label="Min agreement"
              value={<span className="tabular-nums">{r.quoteGate.minAgreement}</span>}
            />
            <Field
              label="Counted providers"
              value={r.quoteGate.countedProviderIds.join(' · ') || 'none'}
            />
          </div>

          {r.quoteGate.uncountedProviderIds.length > 0 && (
            <p className="mt-3 text-xs text-white/35">
              Not counted toward agreement:{' '}
              <span className="font-mono text-white/55">
                {r.quoteGate.uncountedProviderIds.join(' · ')}
              </span>
            </p>
          )}

          <div className="mt-4 space-y-3">
            <div>
              <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-white/35">
                Withheld rows — this is the book state
              </div>
              <Keys items={r.quoteGate.withheldRowKeys} empty="Nothing withheld." />
            </div>
            <div>
              <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-white/35">
                Flagged rows
              </div>
              <Keys items={r.quoteGate.flaggedRowKeys} empty="Nothing flagged." />
            </div>
            <div>
              <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-white/35">
                Single-source market keys
              </div>
              <Keys items={r.quoteGate.singleSourceMarketKeys} empty="None." />
            </div>
          </div>

          <div className="mt-4 flex flex-wrap gap-4 text-xs text-white/35">
            <span>
              withheld instances{' '}
              <span className="tabular-nums text-white/60">
                {r.quoteGate.withheldMarketInstances}
              </span>
            </span>
            <span>
              flagged instances{' '}
              <span className="tabular-nums text-white/60">
                {r.quoteGate.flaggedMarketInstances}
              </span>
            </span>
          </div>
          <p className="mt-2 text-xs leading-relaxed text-white/30">
            Those two counts are a TRAFFIC diagnostic, not coverage. They rise with how often the
            board was served, so a quiet hour and a repaired gate read the same — never alert on
            them. The row-key lists above are the state of the book.
          </p>
        </Card>

        <Card title="Catalogue posture">
          <div className="flex flex-wrap items-center gap-2">
            <Pill
              tone={
                cp.state === 'ready' ? 'positive' : cp.state === 'stale' ? 'caution' : 'caution'
              }
            >
              {cp.state.replace('_', ' ')}
            </Pill>
            {cp.syncedAt && (
              <span className="text-xs text-white/40">
                last sync {timeAgo(cp.syncedAt, now)} ·{' '}
                {new Date(cp.syncedAt).toLocaleString('en-AU')}
              </span>
            )}
          </div>

          <p className="mt-3 text-sm leading-relaxed text-white/65">
            {cp.state === 'ready' ? (
              <>
                The catalogue was written under the posture the running code expects. Otherwise-
                placeable bets are not being refused for staleness.
              </>
            ) : cp.state === 'never_synced' ? (
              <>
                No catalogue sync has ever completed under any posture.{' '}
                <span className="text-white/85">That points at the scheduler, not a deploy</span> —
                kept distinct from “stale” for exactly that reason. Placement refuses{' '}
                <span className="font-mono text-[11px]">market_posture_stale</span> until a sync
                lands.
              </>
            ) : (
              <>
                The stored marker was written under a superseded posture, so placement refuses{' '}
                <span className="font-mono text-[11px]">market_posture_stale</span> on every
                otherwise-placeable bet. Read <span className="text-white/85">last sync</span> to
                tell the two causes apart: minutes ago is the designed 15-minute window closing on
                its own after a deploy; hours ago is a stalled scheduler.
              </>
            )}
          </p>

          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <Field
              label="Expected posture id"
              value={<span className="font-mono text-[11px]">{cp.expectedPostureId}</span>}
            />
            <Field
              label="Persisted posture id"
              value={
                <span className="font-mono text-[11px]">{cp.persistedPostureId ?? 'none'}</span>
              }
            />
            <Field label="Persisted mode" value={cp.persistedMode ?? '—'} />
            <Field label="Persisted posture" value={cp.persistedPosture ?? '—'} />
            <Field
              label="Persisted min agreement"
              value={
                <span className="tabular-nums">
                  {cp.persistedMinAgreement === null ? '—' : cp.persistedMinAgreement}
                </span>
              }
            />
            <Field
              label="Persisted providers"
              value={
                <span className={providerDrift ? 'text-brand-gold' : undefined}>
                  {cp.persistedProviderIds.join(' · ') || 'none'}
                </span>
              }
            />
          </div>

          {providerDrift && (
            <p className="mt-3 text-xs leading-relaxed text-brand-gold/80">
              The provider set the marker was written under differs from the set counted now — that
              is which posture input changed.
            </p>
          )}
        </Card>

        {/* ------------------------------------------------------------------
            Adapter health — the only read on this screen that can change
            between two glances, which is why its stale time is 15s.
           ------------------------------------------------------------------ */}
        <Card title="Adapter health">
          <Probe
            loading={health.isLoading}
            failed={health.isError || !health.data}
            onRetry={() => void health.refetch()}
          >
            {health.data && (
              <>
                {(() => {
                  const s = summariseAdapterHealth(health.data.providers);
                  return (
                    <div className="flex flex-wrap items-center gap-2.5">
                      <Pill tone={s.allOk ? 'positive' : 'caution'}>
                        {s.down === 0 ? 'all answering' : `${s.down} of ${s.total} down`}
                      </Pill>
                      {/*
                        The ABSOLUTE fetch time, not an age. `dataUpdatedAt` IS the
                        fetch clock; measuring it against a render clock would re-time
                        the same payload on every unrelated re-render — the exact drift
                        every other age on these screens reads `dataUpdatedAt` to avoid.
                      */}
                      <span className="text-xs text-white/40">
                        probed at {new Date(health.dataUpdatedAt).toLocaleTimeString('en-AU')}
                      </span>
                    </div>
                  );
                })()}

                <div className="mt-4 grid gap-2 sm:grid-cols-2">
                  {health.data.providers.map((p) => (
                    <div
                      key={p.providerId}
                      className="rounded-xl border border-white/[0.07] bg-white/[0.02] p-3"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm text-white/80">{p.providerName}</span>
                        <Pill tone={p.ok ? 'positive' : 'caution'}>{p.ok ? 'ok' : 'down'}</Pill>
                      </div>
                      <div className="mt-1.5 flex flex-wrap gap-3 text-[11px] text-white/35">
                        <span className="font-mono">{p.providerId}</span>
                        <span className="tabular-nums">{p.latencyMs} ms</span>
                        {p.lastSuccessAt && (
                          <span>last ok {timeAgo(p.lastSuccessAt, health.dataUpdatedAt)}</span>
                        )}
                      </div>
                      {p.lastError && (
                        <p className="mt-1.5 break-words text-[11px] leading-relaxed text-red-300/80">
                          {p.lastError}
                        </p>
                      )}
                    </div>
                  ))}
                </div>

                <p className="mt-3 text-xs leading-relaxed text-white/30">
                  Adapter health is deliberately NOT an input to the gate posture. A provider going
                  down mid-window is an availability event and must not stop betting — so a red
                  here does not, on its own, mean placement changed.
                </p>
              </>
            )}
          </Probe>
        </Card>

        {/* Registry configuration — what each adapter DECLARES, not what it did. */}
        <Card title="Provider registry">
          <Probe
            loading={providers.isLoading}
            failed={providers.isError || !providers.data}
            onRetry={() => void providers.refetch()}
          >
            {providers.data && (
              <div className="space-y-3">
                {providers.data.providers.map((p) => (
                  <div
                    key={p.providerId}
                    className="rounded-xl border border-white/[0.07] bg-white/[0.02] p-3"
                  >
                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                      <span className="text-sm text-white/80">{p.providerName}</span>
                      <span className="font-mono text-[11px] text-white/35">{p.providerId}</span>
                    </div>
                    <div className="mt-2">
                      <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-white/35">
                        Sports
                      </div>
                      <Keys items={p.supportedSports} empty="None declared." />
                    </div>
                    <div className="mt-2.5">
                      <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-white/35">
                        Markets quoted
                      </div>
                      <Keys
                        items={Object.entries(p.marketSupport).flatMap(([sport, markets]) =>
                          markets.map((m) => `${sport}:${m}`),
                        )}
                        empty="None declared."
                      />
                    </div>
                  </div>
                ))}
                <p className="text-xs leading-relaxed text-white/30">
                  This is what each adapter says it can QUOTE. What it can RESULT is a separate
                  declaration, and the gap between the two is what the settleability report above
                  measures.
                </p>
              </div>
            )}
          </Probe>
        </Card>

        {/* ------------------------------------------------------------------
            TTL caches — the one WRITE on this screen.
           ------------------------------------------------------------------ */}
        <Card title="TTL caches">
          <Probe
            loading={cache.isLoading}
            failed={cache.isError || !cache.data}
            onRetry={() => void cache.refetch()}
          >
            {cache.data && (
              <>
                <div className="grid gap-3 sm:grid-cols-2">
                  {(
                    [
                      ['Quotes', cache.data.quotes],
                      ['Results', cache.data.results],
                    ] as const
                  ).map(([label, s]) => (
                    <div
                      key={label}
                      className="rounded-xl border border-white/[0.07] bg-white/[0.02] p-3"
                    >
                      <div className="text-sm text-white/80">{label}</div>
                      <div className="mt-2 grid grid-cols-2 gap-2">
                        <Field
                          label="Entries"
                          value={<span className="tabular-nums">{s.size}</span>}
                        />
                        <Field label="Hit rate" value={ratePct(cacheHitRate(s))} />
                        <Field label="Hits" value={<span className="tabular-nums">{s.hits}</span>} />
                        <Field
                          label="Misses"
                          value={<span className="tabular-nums">{s.misses}</span>}
                        />
                        <Field
                          label="Evictions"
                          value={<span className="tabular-nums">{s.evictions}</span>}
                        />
                        <Field
                          label="In flight"
                          value={<span className="tabular-nums">{s.inflight}</span>}
                        />
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-4 flex flex-wrap items-center gap-3">
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => {
                      invalidate.reset();
                      setInvalidateOpen(true);
                    }}
                  >
                    Invalidate both caches
                  </Button>
                  <span className="text-xs text-white/35">
                    Costs rate-limit budget on the next fan-out. Not a refresh button.
                  </span>
                </div>
              </>
            )}
          </Probe>
        </Card>

        {/* Identity correlation — provider disagreements, worded as suspicion. */}
        <Card title="Identity gaps">
          <Probe
            loading={aliasGaps.isLoading}
            failed={aliasGaps.isError || !aliasGaps.data}
            onRetry={() => void aliasGaps.refetch()}
          >
            {aliasGaps.data && (
              <>
                <p
                  className={`text-sm leading-relaxed ${
                    aliasGaps.data.marketAliasTableProblems.length > 0
                      ? 'text-brand-gold/85'
                      : 'text-white/55'
                  }`}
                >
                  {describeAliasTable(aliasGaps.data.marketAliasTableProblems)}
                </p>
                {aliasGaps.data.marketAliasTableProblems.length > 0 && (
                  <ul className="mt-2 space-y-1">
                    {aliasGaps.data.marketAliasTableProblems.map((p) => (
                      <li key={p} className="font-mono text-[11px] text-white/55">
                        {p}
                      </li>
                    ))}
                  </ul>
                )}

                <div className="mt-4">
                  <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-white/35">
                    Markets one provider offers and another does not
                  </div>
                  {aliasGaps.data.marketGaps.length === 0 ? (
                    <p className="text-sm text-white/35">
                      No single-source disagreements across the current board.
                    </p>
                  ) : (
                    <ul className="space-y-1.5">
                      {aliasGaps.data.marketGaps.map((g) => (
                        <li
                          key={`${g.fixtureId}:${g.marketKey}:${g.providerId}`}
                          className="text-xs leading-relaxed text-white/55"
                        >
                          {describeMarketGap(g.marketKey, g.missingProviders)}{' '}
                          <span className="font-mono text-[10px] text-white/25">
                            {g.fixtureId} · offered by {g.providerId}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <div className="mt-4">
                  <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-white/35">
                    Fixtures that may be the same event under two identities
                  </div>
                  {aliasGaps.data.gaps.length === 0 ? (
                    <p className="text-sm text-white/35">No near-kickoff identity collisions.</p>
                  ) : (
                    <div className="space-y-2">
                      {aliasGaps.data.gaps.map((g) => (
                        <div
                          key={`${g.sport}:${g.kickoffMinute}`}
                          className="rounded-xl border border-white/[0.07] bg-white/[0.02] p-3"
                        >
                          <div className="text-xs text-white/55">
                            {g.sport} · kickoff minute{' '}
                            <span className="font-mono text-white/70">{g.kickoffMinute}</span>
                          </div>
                          <ul className="mt-1.5 space-y-0.5">
                            {g.fixtures.map((f) => (
                              <li
                                key={`${f.providerId}:${f.fixtureId}`}
                                className="text-[11px] text-white/45"
                              >
                                <span className="font-mono text-white/30">{f.providerId}</span>{' '}
                                {f.eventName ?? `${f.home ?? '?'} v ${f.away ?? '?'}`}{' '}
                                <span className="font-mono text-white/25">{f.fixtureId}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <p className="mt-3 text-xs leading-relaxed text-white/30">
                  Two providers describing one event under two fixture ids never reach agreement, so
                  the market is withheld and the bet is refused rather than struck on one opinion.
                  This list is where that shows up before a customer meets it.
                </p>
              </>
            )}
          </Probe>
        </Card>
      </div>

      {/*
        The RAW modal, not `ConfirmAction`, and deliberately.

        `POST /admin/odds-engine/cache/invalidate` declares no reason field. A
        mandatory reason box over an endpoint that discards it would promise an
        account of why this was done that no audit row will ever hold.
      */}
      <Modal
        open={invalidateOpen}
        title="Invalidate the odds caches"
        onClose={() => setInvalidateOpen(false)}
        footer={
          invalidate.isSuccess ? (
            <Button variant="secondary" onClick={() => setInvalidateOpen(false)}>
              Close
            </Button>
          ) : (
            <>
              <Button variant="secondary" onClick={() => setInvalidateOpen(false)}>
                Cancel
              </Button>
              <Button
                variant="danger"
                loading={invalidate.isPending}
                onClick={() => invalidate.mutate()}
              >
                Invalidate
              </Button>
            </>
          )
        }
      >
        {invalidate.isSuccess ? (
          <p className="text-sm leading-relaxed text-white/70">
            Both caches were dropped. The counters and the adapter probes on screen have been
            re-read — the next fan-out re-fetches from every provider.
          </p>
        ) : (
          <>
            <p className="text-sm leading-relaxed text-white/70">
              {cache.data
                ? describeCacheInvalidation(cache.data.quotes.size, cache.data.results.size)
                : describeCacheInvalidation(0, 0)}
            </p>
            <p className="mt-3 text-xs leading-relaxed text-white/35">
              This endpoint records no reason, so there is no audit note to write. It is logged as a
              WRITE against your role and nothing more.
            </p>
            {invalidate.isError && <ActionError error={invalidate.error} />}
          </>
        )}
      </Modal>
    </>
  );
}
